/**
 * One coordinated token refresh per browser (documentation/
 * authentication-security-enhancement.md, "Client refresh coordination").
 *
 * - All callers in a tab (401 interceptor, interval, visibility, manual)
 *   share one in-flight promise.
 * - Tabs serialise through a cross-tab lock: `navigator.locks` where
 *   available, otherwise a localStorage lock with a timeout.
 * - After getting the lock, a refresh another tab finished a moment ago is
 *   reused instead of sending the (already rotated) cookie again.
 * - A 401 from /auth/refresh is checked once with /auth/me (another tab may
 *   have won the race and set fresh cookies). Only if that fails too the
 *   session counts as lost.
 *
 * Only timestamps are stored in localStorage. Tokens stay in httpOnly
 * cookies.
 */

/** Must match ACCESS_TOKEN_TTL_SECONDS in the backend (token-types.ts). */
export const ACCESS_TOKEN_TTL_MS = 15 * 60 * 1000;
/** Refresh proactively when the access token expires within this time. */
export const REFRESH_AHEAD_MS = 2 * 60 * 1000;
/** A refresh finished this recently (by any tab) is reused. */
export const RECENT_REFRESH_MS = 10 * 1000;

export const SESSION_RECORD_KEY = "gpfits:auth:session";
export const REFRESH_LOCK_NAME = "gpfits-refresh";
export const FALLBACK_LOCK_KEY = "gpfits:auth:refresh-lock";
const FALLBACK_LOCK_TTL_MS = 25 * 1000;
const FALLBACK_LOCK_WAIT_MS = 30 * 1000;

interface SessionRecord {
  refreshedAt: number;
  accessExpiresAt: number;
}

export class SessionExpiredError extends Error {
  constructor() {
    super("Session expired");
    this.name = "SessionExpiredError";
  }
}

type LockManagerLike = {
  request: <T>(
    name: string,
    options: { mode: "exclusive" },
    callback: () => Promise<T>,
  ) => Promise<T>;
};

export interface RefreshDeps {
  /** POST /auth/refresh; rejects with the axios error. */
  refresh: () => Promise<void>;
  /** GET /auth/me without triggering another refresh; true on 200. */
  probe: () => Promise<boolean>;
  onExpired?: () => void;
  storage?: Storage;
  locks?: LockManagerLike | null;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

function browserLocks(): LockManagerLike | null {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const locks = (nav as unknown as { locks?: LockManagerLike })?.locks;
  return locks && typeof locks.request === "function" ? locks : null;
}

function parse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function readSessionRecord(
  storage: Storage = localStorage,
): SessionRecord | null {
  return parse<SessionRecord>(storage.getItem(SESSION_RECORD_KEY));
}

/** Call after a successful login, 2FA verification or refresh. */
export function recordFreshSession(
  storage: Storage = localStorage,
  now: number = Date.now(),
): void {
  const record: SessionRecord = {
    refreshedAt: now,
    accessExpiresAt: now + ACCESS_TOKEN_TTL_MS,
  };
  storage.setItem(SESSION_RECORD_KEY, JSON.stringify(record));
}

export function clearSessionRecord(storage: Storage = localStorage): void {
  storage.removeItem(SESSION_RECORD_KEY);
}

/** True if the access token expires soon. Unknown expiry counts as soon. */
export function isAccessNearExpiry(
  storage: Storage = localStorage,
  now: number = Date.now(),
): boolean {
  const record = readSessionRecord(storage);
  return !record || record.accessExpiresAt - now <= REFRESH_AHEAD_MS;
}

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

async function withStorageLock<T>(
  fn: () => Promise<T>,
  storage: Storage,
  now: () => number,
  sleep: (ms: number) => Promise<void>,
): Promise<T> {
  const id = `${now()}-${Math.random().toString(36).slice(2)}`;
  const deadline = now() + FALLBACK_LOCK_WAIT_MS;
  const owner = () =>
    parse<{ id: string; expiresAt: number }>(
      storage.getItem(FALLBACK_LOCK_KEY),
    );

  for (;;) {
    const current = owner();
    if (!current || current.expiresAt < now()) {
      storage.setItem(
        FALLBACK_LOCK_KEY,
        JSON.stringify({ id, expiresAt: now() + FALLBACK_LOCK_TTL_MS }),
      );
      await sleep(25); // let a concurrent writer win or lose
      if (owner()?.id === id) break;
    }
    if (now() > deadline) break; // stale lock holder: go on without it
    await sleep(40 + Math.random() * 40);
  }

  try {
    return await fn();
  } finally {
    if (owner()?.id === id) storage.removeItem(FALLBACK_LOCK_KEY);
  }
}

export function createRefreshCoordinator(deps: RefreshDeps) {
  const storage = deps.storage ?? localStorage;
  const now = deps.now ?? Date.now;
  const sleep = deps.sleep ?? defaultSleep;
  const locks = deps.locks === undefined ? browserLocks() : deps.locks;
  let inFlight: Promise<void> | null = null;

  const runLocked = <T>(fn: () => Promise<T>): Promise<T> =>
    locks
      ? locks.request(REFRESH_LOCK_NAME, { mode: "exclusive" }, fn)
      : withStorageLock(fn, storage, now, sleep);

  async function refreshOnce(requestedAt: number): Promise<void> {
    const record = readSessionRecord(storage);
    if (record && record.refreshedAt >= requestedAt - RECENT_REFRESH_MS) {
      return; // another tab (or this one) refreshed a moment ago
    }
    try {
      await deps.refresh();
      recordFreshSession(storage, now());
    } catch (error) {
      if (statusOf(error) !== 401) throw error; // network, 429, 5xx: no logout
      if (await deps.probe().catch(() => false)) return;
      deps.onExpired?.();
      throw new SessionExpiredError();
    }
  }

  return {
    refresh(): Promise<void> {
      if (!inFlight) {
        const requestedAt = now();
        inFlight = runLocked(() => refreshOnce(requestedAt)).finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },
  };
}
